import { XIcon } from "lucide-react";
import { useMediaQuery } from "../../hooks";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { Tooltip } from "../Tooltip";
import { Text } from "../Typography";
import { useDrawerContext } from "./context";
import { DrawerHeaderBaseProps } from "./types";

type Props = DrawerHeaderBaseProps & {
  className?: string;
};

export const DrawerHeader = (props: Props) => {
  const context = useDrawerContext();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const title = props.title ?? context.title;
  const description = props.description ?? context.description;
  const showClose = props.showClose ?? context.showClose ?? true;
  const className = props.className ?? context.headerClassName;
  const { titleId, descriptionId } = context;

  const showCloseButton = showClose && !context.preventClose;

  return (
    <header
      data-drawer-header
      className={cn(
        "relative p-4 border-b border-neutral-500/10 flex items-center justify-between",
        className,
      )}
    >
      <div
        className={cn("flex flex-col gap-1 w-full", showCloseButton && "pe-8")}
      >
        {title && (
          <Text as="h3" id={titleId} size="md" weight="medium">
            {title}
          </Text>
        )}
        {description && (
          <Text id={descriptionId} size="sm" className="text-neutral-500/50">
            {description}
          </Text>
        )}
      </div>
      {showCloseButton && (
        <Tooltip content={t("drawer.closeLabel")} placement="bottom-end">
          <IconButton
            className={cn(
              "absolute",
              isMobile ? "top-0.5 end-1" : "top-1.5 end-1.5",
            )}
            variant="ghost"
            color="neutral"
            size={isMobile ? "lg" : "md"}
            onClick={context.handleDismiss}
            aria-label={t("drawer.closeLabel")}
          >
            <XIcon />
          </IconButton>
        </Tooltip>
      )}
    </header>
  );
};
